
import { format } from "date-fns";
import { CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { User } from "lucide-react";
import { SwappableCard } from "../SwappableCard";
import { ResizableSwappableCard } from "./ResizableSwappableCard";

interface ActiveUser {
  id: string;
  email: string;
  lastActive: string;
}

interface ActiveUsersTableProps {
  activeUsers: ActiveUser[];
  rearrangingEnabled: boolean;
  onRemove?: (id: string) => void;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  onResize?: (id: string, newSize: 'small' | 'medium' | 'large' | 'xlarge') => void;
}

export function ActiveUsersTable({ 
  activeUsers, 
  rearrangingEnabled, 
  onRemove, 
  size = 'medium', 
  onResize 
}: ActiveUsersTableProps) {
  const tableContent = (
    <>
      <CardHeader>
        <CardTitle>Active Users</CardTitle>
        <CardDescription>Recently active users in the system</CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Last Activity</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {activeUsers.length > 0 ? (
              activeUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-2 text-muted-foreground" />
                      {user.email}
                    </div>
                  </TableCell>
                  <TableCell>
                    {format(new Date(user.lastActive), 'MMM dd, HH:mm')}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} className="text-center">
                  No active users
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </>
  );

  if (rearrangingEnabled && onResize) {
    return (
      <ResizableSwappableCard 
        id="active-users" 
        size={size}
        showHandle={rearrangingEnabled} 
        showResizeControl={rearrangingEnabled}
        onRemove={onRemove}
        onResize={(newSize) => onResize("active-users", newSize)}
      >
        {tableContent}
      </ResizableSwappableCard>
    );
  }

  return (
    <SwappableCard id="active-users" showHandle={rearrangingEnabled} onRemove={onRemove}>
      {tableContent}
    </SwappableCard>
  );
}
